// Availability Map (mapbox)
import { getParameterByName } from './_utilities';

export default class {
    // Class constructor.
    constructor(state, city, options) {
        // Mapbox API settings.
        this.style = 'cingg0slx000qagma89hgrawq';
        this.apiKey =
            'pk.eyJ1IjoiY2xlYXJsaW5rIiwiYSI6ImNpbGluZXRyODM5NTN2YW1jNWJmZ3J1d2YifQ.r6pbaCk2-woaceg77tLwFg';
        this.mapPlacesAPI =
            'https://api.mapbox.com/geocoding/v5/mapbox.places/';
        this.mapboxgl = window.mapboxgl || this.mapboxgl;

        // Class variables.
        this.map = null;
        this.state = null;
        this.city = null;
        this.zip = null;

        // Class settings.
        this.options = {};

        // Class map response object.
        this.mapData = {
            region: null,
        };

        // Use Util method to snag zip from URL, if applicable.
        if (getParameterByName('zip')) {
            this.zip = getParameterByName('zip');
        }

        // Check for sanitized string before assigning.
        if (typeof state === 'string') {
            this.state = state;
        }

        // Check for sanitized string before assigning.
        if (typeof city === 'string') {
            this.city = city;
        }

        // Check for valid object before assigning.
        if (typeof options === 'object') {
            this.options = options;
        }
    }

    // Check for touch devices
    /* eslint-disable */
    checkTouch() {
        return (
            'ontouchstart' in window ||
            navigator.MaxTouchPoints > 0 ||
            navigator.msMaxTouchPoints > 0
        );
    }
    /* eslint-enable */

    // Start logic sequence to display the map.
    displayMap() {
        // Query geo coordinates if applicable, otherwise load map with defaults.
        if (typeof this.state === 'string' || typeof this.city === 'string') {
            this.resolveGeo();
        } else {
            this.loadMap();
        }
    }

    // Perform final transitions for map load.
    finalTransitions() {
        // Bind to class variables.
        const map = this.map;
        const data = this.mapData;

        // Check for class region data.
        if (data.region !== null) {
            const pad = 100;

            // Fit map bounds to bounding box data from region data.
            map.fitBounds(data.region.features[0].bbox, {
                padding: { top: pad, bottom: pad, left: pad, right: pad },
                duration: 1500,
                maxZoom: 9,
            });
        } else {
            // Get current zoom level from map.
            const zoom = map.getZoom();

            // Apply modest easing transition to default view.
            map.easeTo({
                bearing: 0,
                duration: 3000,
                pitch: 0,
                zoom: zoom + 0.025,
            });
        }
    }

    // Get geospatial boundaries for geo context.
    getBounds(resolve, reject) {
        let types = 'postcode';
        let query = this.zip;

        // Determine API query type (postcode, region, palce) from class variables.
        if (this.zip !== null) {
            query = this.zip;
            types = 'postcode';
        } else if (this.state !== null && this.city === null) {
            query = `${this.state},us`;
            types = 'region';
        } else if ((this.state && this.city) !== null) {
            query = `${this.state},${this.city}`;
            types = 'place';
        }

        // Create data object with request parameters.
        const data = {
            /* eslint-disable */
            access_token: this.apiKey,
            /* eslint-enable */
            country: 'us',
            types,
        };

        // Construct query for API request.
        const url = `${this.mapPlacesAPI}${query}.json`;

        // Query Mapbox Places API for geocoding.
        $.ajax({
            data,
            dataType: 'json',
            url,
            success: (response) => {
                // Set class object data.
                this.mapData.region = response;
                resolve();
            },
            error: (err) => {
                reject(err);
            },
        });
    }

    // Render a map to container.
    loadMap() {
        // Bind to class object data.
        const data = this.mapData;

        // Set static map options.
        const minZoom = 3;
        const maxZoom = 12;
        // US map boundaries with Alaska and Hawaii.
        const maxBounds = [
            [-179, 10],
            [-15, 71],
        ];

        // Set configurable default map options.
        let lat = 36;
        let long = -99;
        let zoom = 3;
        // Enabled by default but disabled on touch device detection.
        let dragPan = true;

        // Redefine coordinates if region data is set.
        if (data.region !== null) {
            long = data.region.features[0].center[0];
            lat = data.region.features[0].center[1];

            // Adjust zoom levels for state and city.
            if (this.state !== null && this.city === null) {
                zoom = 5;
            } else if (this.state !== null && this.city !== null) {
                zoom = 8;
            }
        }

        // Check if touch device, if so disable map panning.
        if (this.checkTouch()) {
            dragPan = false;
        }

        // Define settings object for mapbox-gl.
        const mapSettings = {
            boxZoom: true,
            center: [long, lat],
            container: 'mapbox',
            dragPan,
            zoom,
            minZoom,
            maxZoom,
            maxBounds,
            scrollZoom: false,
            style: `mapbox://styles/clearlink/${this.style}?optimize=true`,
            dragRotate: false,
            keyboard: false,
            touchZoomRotate: false,
            pitchWithRotate: false,
            attributionControl: false,
        };

        // Set key/token for mapbox.
        this.mapboxgl.accessToken = this.apiKey;

        // Create and render map on page.
        this.map = new this.mapboxgl.Map(mapSettings);

        // Load various Mapbox control widgets.
        this.setControls();
    }

    // Display/remove a provider layer.
    loadProvider(provider) {
        // TODO: String normalization needs to be cleaner
        if (typeof provider !== 'undefined') {
            // Remove source and layer from display.
            this.map.removeSource(`provider-${this.options.provider}`);
            this.map.removeLayer(this.options.provider);
        }

        // Bind layer for local scope.
        let setProvider = '';
        if (typeof provider === 'undefined' || typeof provider !== 'string') {
            setProvider = this.options.provider;
        } else {
            this.options.provider = provider;
            setProvider = provider;
        }

        // Define mapbox tileset to load.
        const source = {
            type: 'vector',
            url: `mapbox://clearlink.${setProvider}`,
        };

        // Add the provider source to the map.
        this.map.addSource(`provider-${setProvider}`, source);

        // Strip dashes from source layer, since our tileset generator does
        const sourceLayer = setProvider.replace(/-/g, '');

        // Define the mapbox layer properties.
        const layer = {
            id: `${setProvider}`,
            type: 'fill',
            source: `provider-${setProvider}`,
            'source-layer': `${sourceLayer}`,
            paint: {
                'fill-color': '#ef4869',
                'fill-opacity': 0.45,
                'fill-outline-color': 'rgba(0, 0, 0, 0)',
            },
        };

        // Add layer to map display.
        this.map.addLayer(layer);
    }

    // Draw outlines on state boundaries.
    outlineState(state) {
        this.map.addSource('states', {
            type: 'vector',
            url: 'mapbox://mapbox.us_census_states_2015?optimize=true',
        });

        this.map.addLayer({
            id: 'states',
            type: 'line',
            source: 'states',
            'source-layer': 'states',
            layout: {
                'line-join': 'round',
                'line-cap': 'round',
            },
            paint: {
                'line-color': '#ef4869',
                'line-opacity': 0.5,
                'line-width': 1.5,
            },
            filter: ['==', 'NAME', state],
        });
    }

    // Draw outlines on zip boundaries.
    outlineZip(zip) {
        this.map.addSource('zips', {
            type: 'vector',
            url: 'mapbox://clearlink.36kfmztl?optimize=true',
        });

        this.map.addLayer({
            id: 'zips',
            type: 'line',
            source: 'zips',
            'source-layer': 'cb_2016_us_zcta510_500k-55mf5f',
            layout: {
                'line-join': 'round',
                'line-cap': 'round',
            },
            paint: {
                'line-color': '#ef4869',
                'line-opacity': 1,
                'line-width': 2,
            },
            filter: ['==', 'ZCTA5CE10', zip],
        });

        // Disabled animation sequence
        // this.rotate();
    }

    // Promise method for resolving geo boundaries.
    resolveGeo() {
        new window.Promise((resolve, reject) => {
            this.getBounds(resolve, reject);
        })
            .then(() => {
                this.loadMap();
            })
            .catch((err) => {
                console.error('_Maps.js => Promise error!');
                console.error(err);
            });
    }

    // Loads mapbox widgets based on configuration options.
    setControls() {
        const options = this.options;

        this.map.on('load', () => {
            if (typeof options === 'object') {
                if (typeof options.provider === 'string') {
                    this.loadProvider();
                }

                if (options.controls) {
                    this.map.addControl(
                        new this.mapboxgl.NavigationControl(),
                        'top-left'
                    );
                }

                // if (options.locate) {
                //   this.map.addControl(new this.mapboxgl.GeolocateControl(
                //     { enableHighAccuracy: false, timeout: 6000 }), 'top-left');
                // }

                if (options.coder) {
                    /* eslint-disable */
                    this.map.addControl(
                        new MapboxGeocoder({
                            accessToken: this.apiKey,
                            autocomplete: false,
                            zoom: 10,
                        })
                    );
                    /* eslint-enable */
                }
            }

            if ((this.state && this.mapData.region) !== null) {
                this.outlineState(this.mapData.region.features[0].text);

                if (this.zip !== null) {
                    this.outlineZip(this.zip);
                }
            }

            // Easing transitions for perceived load fluidity.
            this.finalTransitions();
        });
    }

    // Rotation animation sequence.
    rotate() {
        const map = this.map;
        const zoom = map.getZoom();

        function rotator() {
            map.easeTo({
                bearing: 90,
                duration: 15000,
                pitch: 55,
                zoom: zoom + 1,
            });
            window.setTimeout(() => {
                map.easeTo({
                    bearing: 180,
                    duration: 15000,
                    pitch: 85,
                    zoom: zoom + 1.25,
                });
                window.setTimeout(() => {
                    map.easeTo({
                        bearing: 240,
                        duration: 15000,
                        pitch: 65,
                        zoom: zoom + 1.5,
                    });
                    window.setTimeout(() => {
                        map.easeTo({
                            bearing: 320,
                            duration: 15000,
                            pitch: 15,
                            zoom: zoom + 1,
                        });
                        window.setTimeout(() => {
                            map.easeTo({
                                bearing: -0,
                                duration: 15000,
                                pitch: 0,
                                zoom,
                            });
                        }, 14500);
                    }, 14500);
                }, 14500);
            }, 14500);
        }

        rotator();
    }
}
